import { OrderStatus } from 'src/app/data/constants/orderStatus.enum';
//Campaign Dashboard
import { HttpParams } from '@angular/common/http';
import { environment } from 'src//environments/environment';
import { PaymentOverallStatus, PaymentStatus } from 'src/app/data/constants/payment-status.enum';
import { PaymentAdjustmentType } from 'src/app/data/constants/payment-adjustment-type.enum';

export interface CustomerData {
    Id: number;
    Name: string;
    Office: string;
    Currency: string;
    Country: string;
}

export interface CustomFilter {
    field: string,
    operation: string,
    values: Array<any>
}

export class CampaignDashboardInput {
    my_campaigns: boolean;
    platform_client_id: number;
    customer_id?: number;
    advertiser_id?: number;
    brand_id?: number;
    creative_id?: number;
    offset: number;
    limit: number;
    order_by: string;
    order: string;
    custom_filters: CustomFilter[];
    search?: string;

    constructor({ customerId, advertiserId, brandId }:
        { customerId: number, advertiserId: number, brandId: number }) {
        this.my_campaigns = false;
        this.platform_client_id = environment.platformClientId; //Media Corp
        this.customer_id = customerId;
        this.advertiser_id = advertiserId;
        this.brand_id = brandId;
        this.creative_id = null;
        this.offset = 0;
        this.limit = 20; //(page number - 1) * limit
        this.order_by = 'campaign_id';
        this.order = 'DESC';
        this.custom_filters = [];
        this.search = '';
    }
}

export class CampaignDashboardData {
    data: CampaignDashboardRow[];
    count: number;

    constructor() {
    }
}

export interface CampaignDashboardRow {
    campaign_id: number;
    campaign_name: string;
    budget: number;
    campaign_status: string;
    start_date: CampaignDate;
    end_date: CampaignDate;
    currency_abbreviation: string;
    additional_discount: boolean;
}

interface CampaignDate {
    utc: string;
    local: string;
    timezone: string;
}

//Plan creation
export class PlanCreationInput {
    // url params
    platform_client_id: number;
    // post params
    name: string;
    customer_id: number;
    advertiser_id: number;
    brand_id: number;
    currency: string;
    type: string;

    constructor({ customerId, advertiserId, brandId }:
        { customerId: number, advertiserId: number, brandId: number }) {
        this.platform_client_id = environment.platformClientId;
        this.customer_id = customerId;
        this.advertiser_id = advertiserId;
        this.brand_id = brandId;
        this.currency = 'SGD';
        this.type = 'TV_BUYER_DEAL';
    }
}

export interface PlanCreationData {
    plan_id: number;
    name: string;
    type: string;
    status: string;
    brand_id: number;
    brand_name: string;
    advertiser_id: number;
    advertiser_name: string;
    customer_id: number;
    customer_name: string;
    platform_client_id: number;
    currency?: string;
    executive_manager_id?: any;
    account_manager_id?: string;
    customer_io_number?: any;
    seller_id?: number;
    buyer_id?: number;
    product_id?: number;
    settings?: any;
    frequency_cap?: any;
    author?: any;
    campaigns?: any;
}

//Campaign creation
export class CampaignCreationInput {
    //url param
    plan_id: number;
    //post params
    name: string;
    seller_id: number;
    device_format: string;
    pricing_type: string;
    type: string;

    constructor() {
        this.seller_id = environment.sellerId;
        this.device_format = 'TV_LINEAR';
        this.pricing_type = 'DEMO_CPM';
        this.type = 'TV_BUYER_DEAL';
    }
}

export interface CampaignCreationData {
    campaign_id: number;
    plan_id: number;
    name: string;
    type: string;
    status: string;
    device_format: string;
    pricing_type: string;
    settings: any;
    frequency_cap: any;
    flights: any
    constraints: any;
    priority_level: any;
    eligibility_criteria: any;
    pricing: any;
    serving_options: any;
    seller_id: number;
    buyer_id: number;
}

export class CampaignNameUpdateInput {
    campaign_id: number;
    name: string;
    seller_id: number;
    device_format: string;
    pricing_type: string;
    type: string;

    constructor() {
        this.seller_id = environment.sellerId;
        this.device_format = 'TV_LINEAR';
        this.pricing_type = 'DEMO_CPM';
        this.type = 'TV_BUYER_DEAL';
    }
}

export class CampaignFlightCreationInput {
    campaign_id: number;
    id: number;
    name: string;
    //use Date.toISOString or Date.toJSON
    start_date: string;
    end_date: string;
    status: string;

    constructor() {
        this.id = 0;
        this.name = 'Flight';
        this.status = 'None';
    }
}

//for both creation output and update(put) input
export class CampaignFlightData {
    flight_id: number;
    name: string;
    start_date: string;
    end_date: string;

    constructor() {
        //name is required for saving
        this.name = 'Flight';
    }
}

export class BaseParams {
    search: string;
    order_by: string;
    order: string;
    offset: number;
    limit: number;

    constructor() {
        this.search = '';
        this.order_by = 'name';
        this.order = 'ASC';
        this.offset = 0;
        this.limit = 1000;
    }

    toHttpParams() {
        return new HttpParams()
            .set('search', this.search)
            .set('order_by', this.order_by)
            .set('order', this.order)
            .set('offset', this.offset.toString())
            .set('limit', this.limit.toString());
    }
}

//get segment GET params
export class CampaignIdParam extends BaseParams {
    campaign_id: number;

    toHttpParams(): HttpParams {
        return super.toHttpParams().set('campaignId', this.campaign_id.toString());
    }

    constructor(campaignId: number) {
        super();
        this.campaign_id = campaignId;
    }
}

export interface SegmentData {
    expression_id: number;
    name: string;
    description?: string;
    expression_value_id?: number;
    strategic_demo?: Array<strategic_demo>;
    behavioral_segments_count?: number;
    universe_estimate_basis_id?: number;
    metadata?: any;
    data_providers?: Array<any>;
    changed_on?: string;
    created_by_user_name?: string;
}

interface strategic_demo {
    strategic_demo_gender?: string;
    strategic_demo_min_age?: number;
    strategic_demo_max_age?: number;
}

export class CampaignSegmentInput {
    campaign_id: number;
    //only required for save
    expression_id?: number;

    constructor() {
    }
}

export interface CampaignSegmentData {
    expression_id: number;
    name: string;
    expression_value_id: number;
    expression_value: CampaignAttributeCriteria;
    is_predefined: boolean;
}

export const AttributeType = {
    Language: 'Language',
    Channel: 'Network',
    Genre: 'Genre'
};

export class AttributesInput {
    platform_client_id: number;
    attribute_type: string;
    attribute_record_type: string;
    attribute_requester: string;
    additional_filters: string;
    attribute_hierarchy_filters?: Array<AttributeHierarchyFilter>;

    constructor() {
        this.platform_client_id = environment.platformClientId;
        this.attribute_record_type = 'Supply';
        this.attribute_requester = 'Demand';
    }
}

export class AttributeHierarchyFilter {
    ids: Array<string>;
    exclude: Boolean;
    attribute_type: string;
    constructor() {
    }
}


//used for demo, channel, genre, language
export interface AttributesData {
    id: string;
    type: string;
    name: string;
    description: string;
    parent: any;
    custom: any;
}

export class DemoOptionsParams extends BaseParams {
    platformClientId: number;
    attributeRecordType: string;
    attributeType: string;
    attributeRequester: string;
    additionalFilters: string;
    universeId: string;

    constructor() {
        super();
        this.platformClientId = environment.platformClientId;
        this.additionalFilters = 'DEMAND_DEVICE_TYPE:TV;DEMAND_FORMAT_TYPE:LINEAR';
        this.attributeRecordType = 'Audience';
        this.attributeRequester = 'Demand';
        this.attributeType = 'DEMO';
        this.universeId = 'SG_TAM';
        this.search = 'BUY';
    }

    toHttpParams(): HttpParams {
        return super.toHttpParams()
            .set('platformClientId', this.platformClientId.toString())
            .set('additionalFilters', this.additionalFilters)
            .set('attributeRecordType', this.attributeRecordType)
            .set('attributeRequester', this.attributeRequester)
            .set('attributeType', this.attributeType)
            .set('universeId', this.universeId)
    }
}

export class CampaignAttributeConstraint {
    entity_id?: number;
    entity_type?: string;
    constraint_id?: string;
    constraint_type_id: string;
    name?: string;
    rank?: number;
    min_value?: number;
    max_value?: number;
    metric_data_class: string;
    metric_data_type: string;
    metric_id?: string;
    violated?: boolean;
    is_enabled?: boolean;
    basis?: any;
    criteria: CampaignAttributeCriteria;

    constructor() {
    }
}

export class CampaignAttributeCriteria {
    negative?: boolean;
    type: string;
    values: Array<CampaignAttributeValue>;

    constructor() {
        this.negative = false;
    }
}

export class CampaignAttributeValue {
    negative?: boolean;
    type: string;
    values: Array<{ id: string }>;

    constructor() {
        this.negative = false;
    }
}

export class CampaignGoalConstraintInput {
    goal: number;
    type_id: number;

    constructor() {
        this.type_id = 2;
    }
}

export class CampaignGoalConstraint extends CampaignGoalConstraintInput {
    id: number;
    goal_unit_of_measure: string;

    constructor() {
        super();
        this.goal_unit_of_measure = "Currency";
    }
}

export class CampaignBudgetUpdateInput {
    //url param
    campaign_id: number;
    //put params
    spend: number;
}

export interface CampaignBudgetData {
    spend: number;
    cpu: number;
    currency: string;
    type: string;
    units: number;
    is_auto_allocated: boolean;
}

export interface CampaignObjectiveData {
    id: number;
    type_id: number;
}

export class CampaignObjectiveCreationInput {
    //url param
    campaign_id: number;
    //post params
    type_id: number;
}

export class CampaignObjectiveUpdateInput {
    //url param
    campaign_id: number;
    campaign_objective_id: number;
    //put params
    type_id: number;
}

export class PrebuiltCampaignData {
    template_id: number;
    name: string;
    description: string;
    content: {
        objective: {
            type_id: number;
        }
        channels: CampaignAttributeConstraint;
        audience?: CampaignAttributeCriteria;
        audience_id?: number;
    }
}

export interface CampaignStatusUpdateInput {
    status: string,
    // date string
    timestamp: string,
    updated_by?: string,
    note?: string
}

export interface CampaignCreativePaymentStatus {
    payment_status: PaymentOverallStatus;
    amount: number;
    payments: Array<{
        payment_id: string;
        // currently only MASTERCARD
        gateway_type: string;
        status: PaymentStatus;
        order_id: string;
        transaction_id: string;
        transaction_timestamp: string;
        amount: number;
        currency_id: number;
        currency_abbreviation: string;
    }>;
}

export class TotalAudienceCriteria {
    universe_estimate_basis: string;
    expression_value_id: number;
    expression_value: CampaignAttributeCriteria;
}

export class AudienceSizeInput {
    demo_universe: string;
    targeting_criteria: CampaignAttributeCriteria;
    total_targeting_criteria: CampaignAttributeCriteria;

    constructor() {
        this.demo_universe = "SG_TAM";
    }
}

export interface AudienceSizeData {
    targeted_audience_size: number;
    total_audience_size: number;
}

export interface AudienceExpressionsData {
    expression_id: number,
    name: string,
    context: string,
    owner_id: number,
    owner_type: string,
    status: string,
    expression_value_id: number,
    expression_value: CampaignAttributeCriteria;
}

export class MediaPlanOptionsInput {
    channel: any;
    genre: any;
    budget: number;
    goal: string;
    campaign_start_date: string;
    campaign_end_date: string;
    is_prime_slot: boolean;
    constructor() {
        this.is_prime_slot = false;
        this.goal = 'demo';
    }
}

export interface MediaPlanOptionData {
    description: string;
    cpm: number;
    impressions: number;
    estimated_spots: number;
}

export class CampaignMediaPlanData {
    media_plan_id: number;
    media_plan_name: string;
    provider: string;
    status: string;
    media_plan_type: string;
    feasible: any;
    budget: number;
    total_cpm: number;
    total_impressions: number;
    demo_cpm: number;
    demo_impressions: number;
    target_cpm: number;
    target_impressions: number;
    currency: string;
    uploaded_on: string;
    expiration_date: string;
    attachment: {
        id: string;
        name: string;
        url: string;
        posted_by: string;
        timestamp: string
    };
    blueprint_link: string
}

export interface CampaignOrder extends CampaignPricingSummary {
    order_id: string;
    session_id: string;
    webhook_type: 'MASTERCARD' | 'BY_PASS';
    payment_gateway: 'MASTERCARD' | 'MEDIACROP_CREDIT' | 'AMOBEE_GATEWAY';
    status: OrderStatus
}

export interface CampaignPricingSummary {
    discount_code_id?: string;
    discount_code?: string;
    currency_id: number;
    currency_abbreviation: string;
    entities: Array<{
        entity_id: number;
        entity_type: 'CAMPAIGN' | 'MEDIA';
        budget: number;
    }>;
    adjustments?: Array<{
        adjustment_type: PaymentAdjustmentType;
        value: number
    }>;
    payment_amount: number;
}

export interface CampaignPricingSummaryBody {
    currency_id: number;
    discount_code?: string;
    entities: Array<{
        entity_id: number;
        entity_type: 'CAMPAIGN' | 'MEDIA'
    }>
}

export interface MediaPlanStatusInput {
    status: string
}

export interface MediaPlanStatusResponse {
    note?: string,
    status: string,
    timestamp: string,
    updated_by?: string
}

export interface AvailsData {
    code: string;
    start_at: string;
}

export interface CampaignSettingsData {
    allow_launch_without_creatives: boolean
}

export interface PacingReportData {
    summary: {
        report_date: string;
        estimated_plan_stats: {
            budget: number,
            units: number,
            total_impressions: number,
            demo_impressions: number,
            demo_cpm: number,
            target_impressions: number,
            target_index: number,
            target_cpm: number
        };
        actual_plan_stats: {
            budget: number,
            units: number,
            total_impressions: number,
            demo_impressions: number,
            demo_cpm: number,
            target_impressions: number,
            target_index: number,
            target_cpm: number
        };
    }
}
